import React, { Component } from 'react'
import ReactQuill, {Quill} from 'react-quill';
import AutoLinks from 'quill-auto-links';
import _ from 'lodash';
import { AttachmentIcon, LoadingIcon } from './Icons';
import "react-quill-emoji";
import "quill-mention";
import classnames from 'classnames';
import { Upload } from 'antd';
import FindingAPI from 'restAPI/FindingAPI';
import { fileToBase64, removeNonPrintableUnicodeCharacters } from 'utils/fileUtils';
import UploadFileList from './UploadFileList';
import AttachmentHandler from '../quill/attachment/AttachmentHandler';
import ImageBlot from '../quill/image/ImageBlot';
import message from './CustomMessage';
import { convertHtmlCommentToMarkDown, getMentionUserIds, processHtmlCommentBeforeSubmit } from 'utils/commentUtil';

Quill.register(ImageBlot);
Quill.register('modules/autoLinks', AutoLinks);

Quill.register('modules/attachmentHandler', AttachmentHandler);

const TOOL_BAR_OPTIONS = {
    emphasis: ['bold', 'italic', 'underline', 'strike'],
    lists: ['ordered', 'bullet'],
    headers: ['1', '2']
};

export default class MessageEditor extends Component {
    ref = React.createRef()
    static defaultProps = {
        hideToolBarOptions: undefined,
        alwaysOpen: false,
        placeholder: "Add a comment...",
        maxLimit: 4000,
        checkCharLimit: true,
        mention: true,
        uploadBtn: true,
        allowCodeSnippet: false,
        onChange: _.noop,
        submitText: 'Comment',
        showFooterButtons: true,
        toolBarIdentifier: 'toolbar', // To uniquely identify the toolbar button when multiple editors are present,
        maxFileSize: 50,
        extraFormats: ["image"]
    }
    formats = [
        "bold",
        "italic",
        "underline",
        "strike",
        "size",
        "color",
        "background",
        "mention",
        "list",
        "bullet",
        "link",
        "emoji",
        "script",
        "header",
        "blockquote",
        "code-block",
        "indent",
        "align",
        "clean",
        "attachment",
    ]
    state = {
        editorHtml: "",
        focused: false,
        submitting: false,
        attachments: [],
        uploadDoc:{},
        uploading: false,
        defaultClassName: "message-editor"
    }
    editorRef = React.createRef()
    constructor(props){
        super(props);

        _.bindAll(
            this,
            "onKeyDown",
            "onSubmit",
            "renderMentionSource",
            "dummyUploadRequest",
            "handleChange",
            "updateAttachments",
            "fileUploaded"
        )
        this.focus = this.focusBlur.bind(this, true);
        this.blur = this.focusBlur.bind(this, false);

        this.modules = {
            autoLinks: true,
            toolbar: {
                container: `#${props.toolBarIdentifier}`,
            },
            "emoji-toolbar": true,
            attachmentHandler: {
                upload: (file) => {
                    this.setState({
                        uploading: true
                    })
                    // return a Promise that resolves in a link to the uploaded image
                    return new Promise((resolve) => {
                        this.props.attachmentV2Handler(file)
                            .then(url => resolve(url));
                    }).finally(() => {
                        this.setState({
                            uploading: false
                        })
                    });
                },
              },
        }
        if(props.mention){
            this.modules.mention = {
                allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
                mentionDenotationChars: ["@",],
                source: this.renderMentionSource
            }
        }
        this.state.editorHtml = props.content || props.value || '';
        this.state.attachments = props.attachments || [];

        this.isEditorset = false;
    }
    messageContentFocus() { // To focus on the content end char
        if (this.editorRef.current && this.props.autoFocus) {
            const node = this.editorRef.current;
            const len = node.value.length;
            node.setEditorSelection(node.editor, { index: len, length: len });
        }
    }
    renderMentionSource(searchTerm, renderItem, mentionChar) {
        const { users } = this.state;
        if(_.isEmpty(users)){
            return;
        }
        let values;
        if (mentionChar === "@") {
          values = users;
        }
        if (searchTerm.length === 0) {
          renderItem(values, searchTerm);
        } else {
          const matches = [];
          for (let i = 0; i < values.length; i++)
            if (
              ~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase())
            )
              matches.push(values[i]);
          renderItem(matches, searchTerm);
        }
    }
    focusBlur(focused){
        this.setState({focused}, () => {
            if(focused && this.editorRef.current){
                this.editorRef.current.focus();
            }
        });

        if(!focused) {
            this.setState({
                uploadDoc: Object.assign({},{}),
            }); 
        }
    }
    removeAttrs(_dom){
        _.each(_dom.attributes, (attr) => {
            if(attr?.name){
                _dom.removeAttribute(attr.name);
            }
        })
        for(const i in _dom.dataset){
            if(_dom.dataset[i]){
                _dom.removeAttribute(`data-${i}`);
            }
        }
    }
    parseBeforeSubmit(htmlString){
        return processHtmlCommentBeforeSubmit(htmlString);
    }
    getMentionUserIds(htmlString){
        return getMentionUserIds(htmlString);
    }
    async onSubmit(){
        this.setState({
            submitting: true
        })
        try {
            const { editorHtml, attachments } = this.state;
            const parsedHtml = this.parseBeforeSubmit(editorHtml);
            const markdownText = convertHtmlCommentToMarkDown(parsedHtml);
            const mentionUserIds = this.getMentionUserIds(editorHtml);

            await this.props.onSubmit({
                htmlText: parsedHtml ? parsedHtml :  _.isEmpty(attachments) ? parsedHtml : "<span></span>",
                markdownText:  markdownText  ? markdownText :  "#",
                mentionUserIds,
                attachments
            });
            this.setState({
                editorHtml: "",
                uploadDoc: Object.assign({},{}),
                attachments: []
            })
        } catch (e) {
            console.log(e)
        } finally {
            this.setState({
                submitting: false
            })
        }
    }
    componentDidMount(){
        if(this.props.ref){
            if(_.has(this.props.ref, "current")){
                this.props.ref.current = this.editorRef.current;
            }
        }
        if(this.props.editorHeight){
            this.ref.current.getElementsByClassName("ql-editor")[0].style.height = this.props.editorHeight + "px"
        }
        this.addImageHandler();
    }
    
    componentDidUpdate(prevProps){
        if(!this.state.users || (prevProps.users?.length !== this.props.users?.length)){
            this.setState({
                users: (this.props.users || []).map(u => ({id: u.id, value: u.displayName}))
            });
        }
    }
    shouldComponentUpdate(nextProps, nextState){
        if(!_.isEqual(this.state, nextState) || nextProps.users?.length !== this.props.users?.length){
            return true;
        }
        return false;
    }
    onKeyDown(e){
        if (this.props.alwaysOpen) {
            return;
        }
        if(e.keyCode === 27){
            e.stopPropagation();
            this.blur();
        }
    }
    handleChange(html){
        let _string = html;
        if(html === '<p><br></p>' || html === '<p><br/></p>') {
            _string = html.replace(/<p><br><\/p>|<p><br\/><\/p>/g, "");
            this.setState({ editorHtml: _string });
        } else {
            this.setState({ editorHtml: html });
        }
        this.props.onChange(_string);
    }
    async dummyUploadRequest({file}){
        // This method will get called equal to the number of files
        const { findingId } = this.props;
        this.setState({
            uploading: true
        })
        try {
            const {id, signedUrl} = await FindingAPI.uploadAttachment(findingId, {
                fileName: removeNonPrintableUnicodeCharacters(file.name)
            }).promise;
            const { attachments } = this.state;
            this.setState({
                // uploadDoc will only hold the last uploadAttachment success file
                uploadDoc: Object.assign({},{name: file.name, id, signedUrl, file}),
                attachments :[...attachments, {name:file.name, id}]
            })
        } catch (e) {
            if(e.response?.data?.errors[0].defaultMessage.includes('fileName should be of size 1-50')) {
                message.error("File name should be of 50 characters");
            }
            console.error(e)
        }
    }

    renderUploadButton(){
        if(!this.props.uploadBtn){
            return null;
        }
        const uploadProps = {
            multiple: true,
            beforeUpload: false,
            customRequest: this.dummyUploadRequest,
            fileList: [],
        }
        return (
            <button className="custom-actions">
                <Upload {...uploadProps}>
                    <AttachmentIcon />
                </Upload>
            </button>
        )
    }

    updateAttachments(_attachments, clearUploadDoc){
        this.setState({
            attachments:_attachments
        });
        if(clearUploadDoc) {
            this.setState({
                uploadDoc: Object.assign({},{}),
            });
        }
    }
    fileUploaded(){
        this.setState({
            uploading: false
        })
    }

    formatToolBarOptions(toolBarOptions) {
        return _.difference(toolBarOptions, this.props.hideToolBarOptions);
    }

    renderToolBar(limit, fileLimit){
        const { submitText, showFooterButtons, toolBarIdentifier, hideToolBarOptions } = this.props;
        const { submitting, uploading , attachments, editorHtml } = this.state;
        const { emphasis, lists, headers } = TOOL_BAR_OPTIONS;
        const emphasisOptions = hideToolBarOptions ? this.formatToolBarOptions(emphasis) : emphasis;
        const isEditorLength = editorHtml.trim().length === 0;
        const attachmentsLength = attachments.length === 0;
        const commentsEmpty = isEditorLength && attachmentsLength
        return (
            <div id={toolBarIdentifier}>
                <span className="ql-formats">
                    {_.map(emphasisOptions, (option) => (
                        <button className={`ql-${option}`} />
                    ))}
                </span>
                
                <span className="ql-formats">
                    {_.map(headers, (option) => (
                        <button className="ql-header" value={option} />
                    ))}
                </span>

                <span className="ql-formats">
                    {_.map(lists, (option) => (
                        <button className="ql-list" value={option} />
                    ))}
                </span>
                {
                    this.props.allowCodeSnippet && (
                        <span className="ql-formats">
                            <button className="ql-code-block" />
                        </span>
                    )
                }
                {!this.props.hideEmoji && <span className="ql-formats">
                    <button className="ql-emoji" />
                </span>}
                {!this.props.hideOldUpload && <span className="ql-formats">
                    {this.renderUploadButton()}
                </span>}
                {
                    this.props.attachmentV2Handler
                    &&
                    <span className="ql-formats">
                        <button className="ql-attachment" />
                    </span>
                }
                {
                    showFooterButtons && (
                        <span className="ql-formats pull-right m-r-0">
                            <div className="ant-btn ant-btn-link ant-btn-sm m-r-xs" onClick={this.props.onCancel || this.blur}>
                                Cancel
                            </div>
                            <div className={classnames("ant-btn ant-btn-primary ant-btn-sm", { "ant-btn-loading": submitting }, {"disabled": limit || uploading || fileLimit || commentsEmpty })} style={{background: limit || uploading || commentsEmpty ? "#d9d9d9":"", borderColor: limit || uploading || commentsEmpty ? "#d9d9d9":""}}  onClick={ (((limit || uploading || commentsEmpty)) && !submitting) || this.props.disableEditor? _.noop : this.onSubmit}>
                                {submitting && <LoadingIcon />}&nbsp;{submitText}
                            </div>
                        </span>
                    )
                }
            </div>
        )
    }

    setEditorValue(htmlValue) {
        this.setState({editorHtml: htmlValue})
    }
    
    render() {
        const { focused, editorHtml, uploadDoc , defaultClassName, attachments} = this.state;
        const { placeholder, maxLimit, alwaysOpen, className, disableEditor, maxFileSize, checkCharLimit } = this.props;
        const baseClassName = classnames(defaultClassName, className);

        const _htmlDoc = document.createElement("div");
        _htmlDoc.innerHTML = editorHtml;
        const limit = checkCharLimit && _.trim(_htmlDoc.innerText).length > maxLimit;

        if(!alwaysOpen && !focused){
            return (
                <div className={baseClassName}>
                    <div className="no-focus-placeholder" onClick={this.focus}>
                        {_.trim(_htmlDoc.innerText).length > 0 ? _htmlDoc.innerText : placeholder}
                    </div>
                </div>
            )
        }
        const fileLimit = uploadDoc.file?.size /1000000 > maxFileSize
        const allFormats = [...this.formats, ...this.props.extraFormats];
        return (
            <div ref={this.ref} className={baseClassName} data-editor-type="text-editor" onKeyDown={this.onKeyDown} style={this.props.style}>
                <ReactQuill
                    value={this.state.editorHtml}
                    onChange={this.handleChange}
                    placeholder={placeholder}
                    modules={this.modules}
                    formats={allFormats}
                    ref={this.editorRef}
                    readOnly={disableEditor}
                    bounds={`[data-editor-type="text-editor"]`}
                 />
                    <div id="ql-attachmentArea">
                       <UploadFileList uploadDoc={uploadDoc} updateAttachments={this.updateAttachments} fileUploaded={this.fileUploaded} attachments={attachments}/>
                   </div>
                {this.renderToolBar(limit, fileLimit, allFormats)}
                {
                    limit? 
                    <div className="d-flex flex-column" style={{color: "red"}}>* Maximum length is {maxLimit} characters</div> : ""
                }
                {
                    fileLimit ? 
                    <div className="d-flex flex-column" style={{color: "red"}}>* Maximum file size is {maxFileSize} MB</div> : ""
                }
            </div>
        )
    }

    addImageHandler(){

        const allFormats = [...this.formats, ...this.props.extraFormats]
        if((allFormats.includes("image") && !_.isEmpty(this.editorRef.current) && !this.isEditorset)){
            this.isEditorset = true;
            const { editor } = this.editorRef.current;
            editor.getModule('toolbar').addHandler('image', () => {
                selectLocalImage(editor);
            });
        }
        this.messageContentFocus();
    }
}

function selectLocalImage(editor) {
    const input = document.createElement('input');
    input.setAttribute('accept', 'image/*');
    input.setAttribute('type', 'file');
    input.click();

    // Listen upload local image and save to server
    input.onchange = () => {
        const file = input.files[0];
        // file type is only image.
        if (/^image\//.test(file.type)) {
            insertToEditor(editor, file);
        } else {
            console.warn('You could only upload images.');
        }
    };
}

async function insertToEditor(editor, file) {
    try {
        // push image url to rich editor.
        const src = URL.createObjectURL(file);
        const base64 = await fileToBase64(file);
        const range = editor.getSelection();
        editor.insertEmbed(range.index, 'image', { src, 'data-image': base64 });
    } catch(e) {
        console.error('Error in converting image file to base64', e);    
    }
}
